export type MotorLibraryCollection = {
  id: string;
  title: string;
  subtitle: string;
  purposeFilter: 'all' | 'rulon' | 'razdv' | 'plisse' | 'jaluzi' | 'zip' | 'markiza';
  categoryKeywords: string[];
};

export type MotorTechSpec = {
  title: string;
  protocols: string[];
  voltage?: string;
  power?: string;
  torque?: string;
  speed?: string;
  noise?: string;
  ipClass?: string;
  temperature?: string;
  notes?: string[];
  models?: MotorTechModel[];
};

export type MotorTechModel = {
  name: string;
  control: string;
  voltage?: string;
  power?: string;
  torque?: string;
  speed?: string;
  noise?: string;
  ipClass?: string;
  temperature?: string;
  notes?: string[];
};

export type MotorHandbookModel = {
  name: string;
  summary?: string;
  specs: string[];
};

export type MotorHandbookSection = {
  id: string;
  title: string;
  sourceUrl: string;
  models: MotorHandbookModel[];
};

export function motorHandbookModelKey(sectionId: string, modelName: string): string {
  const norm = (s: string) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9а-яё]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  return `${norm(sectionId)}.${norm(modelName)}`;
}

export const MOTOR_LIBRARY_COLLECTIONS: MotorLibraryCollection[] = [
  {
    id: 'electro-cornice',
    title: 'Двигатели для электрокарнизов',
    subtitle:
      'Подбор по длине трека, типу управления и сценариям автоматизации. Базовый выбор для римских, рулонных и раздвижных решений.',
    purposeFilter: 'rulon',
    categoryKeywords: ['рулон', 'рим', 'раздвиж'],
  },
  {
    id: 'tube',
    title: 'Внутривальные двигатели',
    subtitle:
      'Скрытая интеграция в вал, аккуратный внешний вид и тихая работа. Подходят для современных интерьеров и минималистичных систем.',
    purposeFilter: 'all',
    categoryKeywords: ['рулон', 'zip', 'маркиз'],
  },
  {
    id: 'vertical',
    title: 'Двигатели для вертикальных жалюзи',
    subtitle:
      'Точная регулировка поворота ламелей и положений, поддержка сценариев день/ночь и централизованного управления.',
    purposeFilter: 'jaluzi',
    categoryKeywords: ['жалюзи'],
  },
  {
    id: 'plisse',
    title: 'Двигатели для плиссе',
    subtitle:
      'Компактные решения для нестандартных проемов, мансард и сложной геометрии. Фокус на мягком ходе и точной остановке.',
    purposeFilter: 'plisse',
    categoryKeywords: ['плиссе'],
  },
];

export const MOTOR_TECH_REFERENCE: MotorTechSpec[] = [
  {
    title: 'MR-2234',
    protocols: ['RF 433', 'Сухие контакты'],
    voltage: '100-240В, 50/60Гц',
    power: '65Вт',
    torque: 'до 2,5 Н*м (в зависимости от версии)',
    speed: '16 см/с, до 112 об/мин',
    noise: '<15 дБ',
    ipClass: 'IP40/IP41 (в зависимости от версии)',
    temperature: 'от -10°C до +55°C',
    notes: [
      'Есть версии Wi-Fi (Tuya), ZigBee (Tuya), Matter, фазное, RS-485.',
      'Для Wi-Fi Tuya не используется Xiaomi Home; для ZigBee нужен ZigBee-шлюз.',
    ],
    models: [
      {
        name: 'MR-2234',
        control: 'RF 433 + сухие контакты',
        voltage: '100-240В, 50/60Гц',
        power: '65Вт',
        torque: '0,3 А (номинальный ток)',
        speed: '16 см/с',
        noise: '<15 дБ',
        ipClass: 'IP40',
      },
      {
        name: 'MR-2234 Wi-Fi / ZigBee / Matter',
        control: 'RF 433 + smart-протокол',
        voltage: '100-240В, 50/60Гц',
        power: '30-65Вт',
        torque: '1,2-1,5 Н*м',
        speed: '12-16 см/с',
        noise: '<15-35 дБ',
        ipClass: 'IP40/IP41',
      },
    ],
  },
  {
    title: 'MR-2256',
    protocols: ['RF 433', 'Сухие контакты', 'Wi-Fi (Tuya/Xiaomi)', 'RS-485'],
    voltage: '100-240В, 50/60Гц',
    power: '56Вт',
    torque: '2,5 Н*м',
    speed: '120 об/мин',
    noise: '<15 дБ',
    ipClass: 'IP41',
    temperature: 'от -10°C до +55°C',
    notes: [
      'Есть версии под Smart Life (Tuya), Xiaomi Home и Modbus RTU (RS-485).',
      'Комплект включает мотор, инструкцию и монтажные элементы.',
    ],
    models: [
      {
        name: 'MR-2256',
        control: 'RF 433 + сухие контакты',
        voltage: '100-240В, 50/60Гц',
        power: '56Вт',
        torque: '2,5 Н*м',
        speed: '120 об/мин',
        noise: '<15 дБ',
        ipClass: 'IP41',
      },
      {
        name: 'MR-2256 Wi-Fi (Tuya/Xiaomi)',
        control: 'RF 433 + Wi-Fi 2,4ГГц',
        voltage: '100-240В, 50/60Гц',
        power: '56Вт',
        torque: '2,5 Н*м',
        speed: '120 об/мин',
        noise: '<15 дБ',
        ipClass: 'IP41',
      },
      {
        name: 'MR-2256 RS-485',
        control: 'RF 433 + RS-485 (Modbus RTU)',
        voltage: '100-240В, 50/60Гц',
        power: '56Вт',
        torque: '2,5 Н*м',
        speed: '120 об/мин',
        noise: '<15 дБ',
        ipClass: 'IP41',
      },
    ],
  },
  {
    title: 'MR-2408',
    protocols: ['RF 433', 'Wi-Fi (Tuya/Xiaomi)'],
    voltage: '12В DC (есть аккумуляторные версии)',
    power: '8,4-18Вт (по модификациям)',
    torque: '0,8 Н*м',
    speed: '34 об/мин (для день/ночь: 20-40 об/мин)',
    noise: '<35 дБ (день/ночь: до 44 дБ)',
    ipClass: 'IP40',
    temperature: 'от 0°C до +40°C',
    notes: [
      'Есть аккумуляторная версия MR-2408A и Wi-Fi-версии Tuya/Xiaomi.',
      'Подходит для легких систем, до 3 кг в типовых конфигурациях.',
    ],
    models: [
      {
        name: 'MR-2408',
        control: 'RF 433',
        voltage: '12В DC',
        power: '10Вт',
        torque: '0,8 Н*м',
        speed: '34 об/мин',
        noise: '<35 дБ',
        ipClass: 'IP40',
        temperature: '0°C ... +40°C',
      },
      {
        name: 'MR-2408A (АКБ)',
        control: 'RF 433, аккумулятор',
        voltage: '8,4В DC',
        power: '8,4Вт',
        torque: '0,8 Н*м',
        speed: '34 об/мин',
        noise: '<35 дБ',
        ipClass: 'IP40',
      },
      {
        name: 'MR-2408 Wi-Fi (Tuya/Xiaomi)',
        control: 'RF 433 + Wi-Fi 2,4ГГц',
        voltage: '12В DC',
        power: '10Вт',
        torque: '0,8 Н*м',
        speed: '34 об/мин',
        noise: '<35 дБ',
        ipClass: 'IP40',
      },
    ],
  },
];

export function getMotorTechReference(name: string): MotorTechSpec | null {
  const n = name.toUpperCase();
  if (n.includes('MR-2234')) return MOTOR_TECH_REFERENCE[0];
  if (n.includes('MR-2256')) return MOTOR_TECH_REFERENCE[1];
  if (n.includes('MR-2408')) return MOTOR_TECH_REFERENCE[2];
  return null;
}

export const MOTOR_HANDBOOK_SECTIONS: MotorHandbookSection[] = [
  {
    id: 'razdvizhnye',
    title: 'Раздвижные электрокарнизы',
    sourceUrl: 'https://zabotaonviz.bitrix24site.ru/tekhnichkadlyadvigateley/razdvi/',
    models: [
      {
        name: 'MR-2234',
        summary: 'Базовый AC-мотор для раздвижных карнизов.',
        specs: [
          'Управление: RF 433 + сухие контакты (RJ-9)',
          'Питание: 100-240В, 50/60Гц; режим S2 (4 мин)',
          'Мощность: 65Вт; скорость: 16 см/с (112 об/мин)',
          'Шум: <15дБ; IP40; температура: -10...+55°C',
          'Габариты: 300x70x50мм (285x70x50мм без вала); вес: ~655г',
        ],
      },
      {
        name: 'MR-2234 Wi-Fi (Xiaomi Home)',
        summary: 'Wi-Fi версия под Xiaomi Home.',
        specs: [
          'Управление: RF 433 + сухие контакты + Wi-Fi 2.4ГГц (Xiaomi Home)',
          'Питание: 100-240В, 50/60Гц; ток: 0.3А; S2 (4 мин)',
          'Мощность: 65Вт; скорость: 16 см/с; 112 об/мин',
          'Шум: <15дБ; IP40; температура: -10...+55°C',
          'Габариты: 300x70x50мм; вес: ~650г',
          'Важно: не совместим с Smart Life',
        ],
      },
      {
        name: 'MR-2234 Wi-Fi (Tuya)',
        summary: 'Wi-Fi версия под Smart Life.',
        specs: [
          'Управление: RF 433 + сухие контакты + Wi-Fi 2.4ГГц (Smart Life)',
          'Питание: 100-240В, 50/60Гц; ток: 0.3А; S2 (4 мин)',
          'Мощность: 65Вт; скорость: 16 см/с; 112 об/мин',
          'Шум: <15дБ; IP40; температура: -10...+55°C',
          'Габариты: 300x70x50мм; вес: ~650г',
          'Важно: не совместим с Xiaomi Home',
        ],
      },
      {
        name: 'MR-2234 ZigBee (Tuya)',
        summary: 'ZigBee-версия для Smart Life экосистем.',
        specs: [
          'Управление: RF 433 + сухие контакты + ZigBee 2.0 (Tuya)',
          'Питание: 100-240В, 50/60Гц; ток: 0.3А; S2 (4 мин)',
          'Мощность: 65Вт; скорость: 16 см/с; 112 об/мин',
          'Шум: <15дБ; IP40; температура: -10...+55°C',
          'Габариты: 300x70x50мм; вес: ~649г',
          'Требуется ZigBee-шлюз Tuya',
        ],
      },
      {
        name: 'MR-2234A ZigBee (Tuya, АКБ)',
        summary: 'Аккумуляторный ZigBee привод.',
        specs: [
          'Управление: RF 433 + ZigBee 2.0 (Smart Life)',
          'Питание: 7-12.6В DC; АКБ 5200mA; S2 (4 мин)',
          'Мощность: 30Вт; момент: 1.2Н*м; скорость: 12 см/с',
          'Шум: <15дБ; IP40; температура: 1...40°C',
          'Габариты: 330x70x50мм; вес: ~886г',
        ],
      },
      {
        name: 'MR-2234 Matter',
        summary: 'Версия с поддержкой Matter.',
        specs: [
          'Управление: RF 433 + Matter + сухие контакты',
          'Питание: 100-240В, 50Гц; ток: 0.46А; S2 (3 мин)',
          'Мощность: 30Вт; момент: 1.5Н*м; скорость: 12 см/с',
          'Шум: <35дБ; IP41; температура: -10...+55°C',
          'Габариты: 240x59x50мм; вес: ~505г',
        ],
      },
      {
        name: 'MR-2234 F4',
        summary: 'Фазное исполнение с повышенным моментом.',
        specs: [
          'Управление: фазное',
          'Питание: 100-240В, 50/60Гц; ток: 0.3А; S2 (4 мин)',
          'Мощность: 65Вт; момент: 2.0Н*м; скорость: 16 см/с',
          'Шум: <15дБ; IP40; температура: -10...+55°C',
          'Габариты: 300x70x50мм; вес: ~680г',
        ],
      },
      {
        name: 'MR-2234 F5',
        summary: 'Фазная версия: RF 433, RS-485 и сухие контакты.',
        specs: [
          'Управление: RF 433 + фазное + RS-485 + сухие контакты',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'Мощность: 65Вт; скорость: 16 см/с; 112 об/мин',
          'Момент: 1.5–2.0 Н*м',
        ],
      },
      {
        name: 'MR-2234 F5 Oval',
        summary: 'Фазная версия Oval: RS-485 Modbus, серый корпус.',
        specs: [
          'Управление: RS-485 (Modbus RTU 9600 8N1)',
          'Корпус: серый; IP20',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'Мощность: 65Вт; скорость: 16 см/с; 112 об/мин',
          'Момент: 1.5–2.0 Н*м',
        ],
      },
      {
        name: 'MR-2234 75Вт',
        summary: 'Усиленная раздвижная версия 75 Вт для тяжёлых полотен.',
        specs: [
          'Управление: RF + фазное + сухие контакты',
          'Мощность: 75Вт; момент: 1.2 Н*м; скорость: 20 см/с',
          'Шум: <45 дБ',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'IP40; температура: -10...+55°C',
          'Вес: ~1330 г',
        ],
      },
      {
        name: 'MR-2234 100Вт',
        summary: 'Усиленная раздвижная версия 100 Вт для тяжёлых полотен.',
        specs: [
          'Управление: RF + фазное + сухие контакты',
          'Мощность: 100Вт; момент: 2.5 Н*м; скорость: 14 см/с',
          'Шум: <45 дБ',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'IP40; температура: -10...+55°C',
          'Вес: ~1880 г',
        ],
      },
    ],
  },
  {
    id: 'vnutrivalnye',
    title: 'Внутривальные двигатели',
    sourceUrl: 'https://zabotaonviz.bitrix24site.ru/tekhnichkadlyadvigateley/rullers/',
    models: [
      {
        name: 'MR-100A',
        summary: 'Аккумуляторный компактный привод Uni-2 для легких рулонных систем.',
        specs: [
          'Управление: RF 433',
          'Напряжение заряда: 5В DC (Micro-USB), ток заряда: 2А',
          'АКБ: 800мАч, мощность: 5Вт',
          'Макс. вес ткани: до 2.5 кг',
          'Момент: 0.3Н*м, скорость: 30 об/мин',
          'Шум: <35дБ, IP20',
          'Размер: 370x29x29мм, вес: 123г',
        ],
      },
      {
        name: 'MR-160',
        summary: 'Базовая внутривальная версия, RF 433.',
        specs: [
          'Управление: RF 433',
          'Напряжение: 100-240В, 50/60Гц; мощность: 12Вт; ток: 1А',
          'Макс. вес ткани: до 5 кг',
          'Момент: 1.1Н*м, скорость: 28 об/мин',
          'Шум: <30дБ, IP44',
          'Размер: 457×44×44 мм, вес: 350–360 г',
        ],
      },
      {
        name: 'MR-160 Wi-Fi (Tuya)',
        summary: 'Wi-Fi 2.4 ГГц, экосистема Smart Life (Tuya).',
        specs: [
          'Управление: RF 433 + Wi-Fi 2.4ГГц (Smart Life / Tuya)',
          'Напряжение: 100-240В, 50/60Гц; мощность: 12Вт',
          'Макс. вес ткани: до 5 кг; момент: 1.1Н*м; 28 об/мин',
          'Шум: <30дБ, IP44; размер: 457×44×44 мм',
          'Важно: не совместим с Xiaomi Home',
        ],
      },
      {
        name: 'MR-160 Wi-Fi (Xiaomi Home)',
        summary: 'Wi-Fi 2.4 ГГц, экосистема Xiaomi Home.',
        specs: [
          'Управление: RF 433 + Wi-Fi 2.4ГГц (Xiaomi Home)',
          'Напряжение: 100-240В, 50/60Гц; мощность: 12Вт',
          'Макс. вес ткани: до 5 кг; момент: 1.1Н*м; 28 об/мин',
          'Шум: <30дБ, IP44; размер: 457×44×44 мм',
          'Важно: не совместим с Smart Life',
        ],
      },
      {
        name: 'MR-160 ZigBee (Tuya)',
        summary: 'ZigBee 2.0, экосистема Smart Life (Tuya).',
        specs: [
          'Управление: RF 433 + ZigBee 2.0 (Tuya)',
          'Напряжение: 100-240В, 50/60Гц; мощность: 12Вт',
          'Макс. вес ткани: до 5 кг; момент: 1.1Н*м; 28 об/мин',
          'Шум: <30дБ, IP44; требуется ZigBee-шлюз Tuya',
        ],
      },
      {
        name: 'MR-160A',
        summary: 'Аккумуляторная версия, зарядка USB-C.',
        specs: [
          'Управление: RF 433',
          'АКБ: 2550 mAh, 8.4 В; мощность: 8.4 Вт',
          'Макс. вес ткани: до 5 кг; момент: 1.1Н*м; 28 об/мин',
          'Шум: <30дБ, IP44; размер: 457×44×44 мм',
        ],
      },
      {
        name: 'MR-160A ZigBee (Tuya)',
        summary: 'АКБ + ZigBee 2.0 (Tuya), зарядка USB-C.',
        specs: [
          'Управление: RF 433 + ZigBee 2.0 (Tuya)',
          'АКБ: 2550 mAh, USB-C; 8.4 В, 8.4 Вт',
          'Макс. вес ткани: до 5 кг; момент: 1.1Н*м; 28 об/мин',
          'Шум: <30дБ, IP44',
        ],
      },
      {
        name: 'MR-160 FC',
        summary: 'Версия с сухим контактом для умного дома.',
        specs: [
          'Управление: RF 433 + сухой контакт',
          'Кабель dry-contact: ~210 см',
          'Макс. вес ткани: до 5 кг; момент: 1.1Н*м; 28 об/мин',
          'Шум: <30дБ, IP44; размер: 457×44×44 мм',
        ],
      },
      {
        name: 'MR-350',
        summary: 'Тяжёлый привод для рулонных, римских и горизонтальных жалюзи.',
        specs: [
          'Управление: RF 433',
          'Напряжение: 100-240В, 50/60Гц; ток: 0.64А',
          'Макс. вес ткани: до 25 кг',
          'Момент: 10Н*м, скорость: 17 об/мин',
          'Шум: <30дБ, IP44',
          'Размер: 595×50×50 мм (труба 35 мм), вес: 1300–1330 г',
        ],
      },
      {
        name: 'MR-350 Wi-Fi (Xiaomi Home)',
        summary: 'Wi-Fi версия для Xiaomi Home.',
        specs: [
          'Управление: RF 433 + Wi-Fi 2.4ГГц (Xiaomi Home)',
          'Напряжение: 100-240В; до 25 кг; момент: 10Н*м; 17 об/мин',
          'Шум: <30дБ, IP44; вес: ~1300 г',
        ],
      },
      {
        name: 'MR-350 ZigBee (Tuya)',
        summary: 'ZigBee версия для Smart Life (Tuya).',
        specs: [
          'Управление: RF 433 + ZigBee 2.0 (Tuya)',
          'Напряжение: 100-240В; до 25 кг; момент: 10Н*м; 17 об/мин',
          'Шум: <30дБ, IP44; требуется ZigBee-шлюз',
        ],
      },
      {
        name: 'MR-350A',
        summary: 'Аккумуляторная версия, 12 В DC.',
        specs: [
          'Управление: RF 433',
          'Питание: 12 В DC; АКБ 4.8 А·ч; мощность: 25.2 Вт',
          'Момент: 6 Н*м, скорость: 24 об/мин',
          'Шум: <30дБ, IP44; температура: 0…+40°C',
        ],
      },
      {
        name: 'MR-350F (фазное управление)',
        summary: 'Фазное управление, до 25 кг.',
        specs: [
          'Управление: фазное',
          'Момент: 10 Н*м, скорость: 17 об/мин',
          'Макс. вес ткани: до 25 кг',
          'Шум: <30дБ, IP44; напряжение: 100-240В',
        ],
      },
      {
        name: 'MR-350FC',
        summary: 'RF 433 + сухие контакты.',
        specs: [
          'Управление: RF 433 + сухие контакты',
          'Момент: 10 Н*м, 17 об/мин; до 25 кг',
          'Шум: <30дБ, IP44; температура: 0…+40°C',
        ],
      },
      {
        name: 'MR-350RS',
        summary: 'RS-485 Modbus RTU.',
        specs: [
          'Управление: RF 433 + RS-485 (Modbus RTU 9600 8N1)',
          'Момент: 10 Н*м, 17 об/мин; до 25 кг',
          'Шум: <30дБ, IP44',
        ],
      },
      {
        name: 'MR-450 с кнопкой управления',
        summary: 'Высокомоментный привод с кнопкой на корпусе.',
        specs: [
          'Управление: кнопка на корпусе + RF',
          'Момент: 50 Н*м, скорость: 12 об/мин',
          'Напряжение: 100-240В; ток: 0.98А',
          'Шум: <30дБ, IP44; температура: 0…+40°C',
        ],
      },
      {
        name: 'MR-450 с механической регулировкой',
        summary: 'Высокомоментный привод с механической регулировкой.',
        specs: [
          'Управление: механическая регулировка + RF',
          'Момент: 50 Н*м, скорость: 12 об/мин',
          'Напряжение: 100-240В',
          'Шум: <30дБ, IP44; температура: 0…+40°C',
        ],
      },
      {
        name: 'MR-450F',
        summary: 'Фазная версия для тяжёлых полотен до 55 кг.',
        specs: [
          'Управление: фазное',
          'Момент: 30 Н*м, скорость: 15 об/мин',
          'Макс. вес полотна: до 55 кг',
          'Напряжение: 100-240В; шум: <30дБ, IP44',
        ],
      },
      {
        name: 'Двигатель для закрытой маркизы',
        summary: 'Уличный привод для закрытой маркизы.',
        specs: [
          'Мощность: 228 Вт',
          'Момент: 50 Н*м, скорость: 12 об/мин',
          'IP44; напряжение: 100-240В',
        ],
      },
      {
        name: 'Двигатель для открытой маркизы',
        summary: 'Уличный привод для открытой маркизы.',
        specs: [
          'Мощность: 205 Вт',
          'Момент: 50 Н*м, скорость: 12 об/мин',
          'IP44; напряжение: 100-240В',
        ],
      },
      {
        name: 'Двигатель для уличной перголы',
        summary: 'Фазный привод для уличной перголы.',
        specs: [
          'Управление: фазное',
          'Мощность: 228 Вт',
          'Момент: 50 Н*м, скорость: 12 об/мин',
          'IP44',
        ],
      },
      {
        name: 'Двигатель для зип системы',
        summary: 'Привод для ZIP-систем.',
        specs: [
          'Мощность: 145 Вт',
          'Момент: 20 Н*м, скорость: 15 об/мин',
          'IP44; напряжение: 100-240В',
        ],
      },
    ],
  },
  {
    id: 'vertical',
    title: 'Для вертикальных жалюзи',
    sourceUrl: 'https://zabotaonviz.bitrix24site.ru/tekhnichkadlyadvigateley/MR-2256/',
    models: [
      {
        name: 'MR-2256',
        summary: 'Базовая модель для вертикальных жалюзи.',
        specs: [
          'Управление: RF 433 + сухие контакты',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'Мощность: 56Вт; момент: 2.5Н*м; скорость: 120 об/мин',
          'Шум: <15дБ; IP41; температура: -10...+55°C',
          'Габариты: 335x44x51мм (315x44x51мм без вала); вес: ~805г',
        ],
      },
      {
        name: 'MR-2256 Wi-Fi (Xiaomi Home)',
        summary: 'Сетевая версия для Xiaomi Home.',
        specs: [
          'Управление: RF 433 + сухие контакты + Wi-Fi 2.4ГГц',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'Мощность: 56Вт; момент: 2.5Н*м; скорость: 120 об/мин',
          'Шум: <15дБ; IP41; температура: -10...+55°C',
          'Важно: не совместим с Smart Life',
        ],
      },
      {
        name: 'MR-2256 Wi-Fi (Tuya)',
        summary: 'Сетевая версия для Smart Life.',
        specs: [
          'Управление: RF 433 + сухие контакты + Wi-Fi 2.4ГГц',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'Мощность: 56Вт; момент: 2.5Н*м; скорость: 120 об/мин',
          'Шум: <15дБ; IP41; температура: -10...+55°C',
          'Важно: не совместим с Xiaomi Home',
        ],
      },
      {
        name: 'MR-2256 RS-485',
        summary: 'Проводная интеграция в BMS/умный дом.',
        specs: [
          'Управление: RF 433 + сухие контакты + RS-485 (Modbus RTU 9600 8N1)',
          'Питание: 100-240В, 50/60Гц; S2 (4 мин)',
          'Мощность: 56Вт; момент: 2.5Н*м; скорость: 120 об/мин',
          'Шум: <15дБ; IP41; температура: -10...+55°C',
          'Габариты: 335x44x51мм; вес: ~805г',
        ],
      },
    ],
  },
  {
    id: 'plisse',
    title: 'Для плиссе',
    sourceUrl: 'https://zabotaonviz.bitrix24site.ru/tekhnichkadlyadvigateley/MR-2408/',
    models: [
      {
        name: 'MR-2408',
        summary: 'Базовый плиссе-привод (12В DC).',
        specs: [
          'Управление: RF 433',
          'Питание: 12В DC; ток: 0.9А; S2 (4 мин)',
          'Мощность: 10Вт; момент: 0.8Н*м; скорость: 34 об/мин',
          'Макс. вес ткани: до 3 кг',
          'Шум: <35дБ; IP40; температура: 0...40°C',
          'Размер мотора: 257x24x24мм; вес: 200г',
        ],
      },
      {
        name: 'MR-2408А',
        summary: 'Аккумуляторная версия с контроллером и USB-C зарядкой.',
        specs: [
          'Управление: RF 433; АКБ 2600mAh (полный заряд >7ч)',
          'Напряжение заряда: 5В DC, 2А; рабочее напряжение: 8.4В DC',
          'Мощность: 8.4Вт; ток: 0.9А; S2 (4 мин)',
          'Момент: 0.8Н*м; скорость: 34 об/мин; вес ткани: до 3 кг',
          'Шум: <35дБ; IP40; температура: 0...40°C',
        ],
      },
      {
        name: 'MR-2408 Wi-Fi (Xiaomi Home)',
        summary: 'Wi-Fi версия для Xiaomi Home.',
        specs: [
          'Управление: RF 433 + Wi-Fi 2.4ГГц',
          'Питание: 12В DC; ток: 0.9А; мощность: 10Вт',
          'Момент: 0.8Н*м; скорость: 34 об/мин; до 3 кг',
          'Шум: <35дБ; IP40; температура: 0...40°C',
          'Важно: совместимость только с Xiaomi Home',
        ],
      },
      {
        name: 'MR-2408 Wi-Fi (Tuya)',
        summary: 'Wi-Fi версия для Smart Life.',
        specs: [
          'Управление: RF 433 + Wi-Fi 2.4ГГц',
          'Питание: 12В DC; ток: 0.9А; мощность: 10Вт',
          'Момент: 0.8Н*м; скорость: 34 об/мин; до 3 кг',
          'Шум: <35дБ; IP40; температура: 0...40°C',
          'Важно: не совместим с Xiaomi Home',
        ],
      },
      {
        name: 'MR-2408 «день-ночь»',
        summary: 'Специализированная версия для систем Day/Night.',
        specs: [
          'Управление: RF 433; двойные кнопки настройки (день/ночь)',
          'Питание: 12В DC; ток: 1.5А; мощность: 18Вт',
          'Момент: 0.8Н*м; скорость: 20-40 об/мин; до 3 кг',
          'Шум: ≤44дБ; IP40; температура: 0...40°C',
          'Размер мотора: 340x25x25мм; вес: ~315г',
        ],
      },
    ],
  },
];

export type MotorHandbookFlatEntry = {
  key: string;
  sectionId: string;
  sectionTitle: string;
  modelName: string;
  summary?: string;
};

export function buildMotorHandbookFlatIndex(): MotorHandbookFlatEntry[] {
  const out: MotorHandbookFlatEntry[] = [];
  for (const section of MOTOR_HANDBOOK_SECTIONS) {
    for (const model of section.models) {
      out.push({
        key: motorHandbookModelKey(section.id, model.name),
        sectionId: section.id,
        sectionTitle: section.title,
        modelName: model.name,
        summary: model.summary,
      });
    }
  }
  return out;
}

export function normalizeMotorLookupToken(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

export function buildMotorNameSlugIndex(): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const entry of buildMotorHandbookFlatIndex()) {
    const slug = normalizeMotorLookupToken(entry.modelName);
    if (!slug) continue;
    const list = index.get(slug) || [];
    if (!list.includes(entry.key)) list.push(entry.key);
    index.set(slug, list);
  }
  return index;
}
